import { Controller } from "@hotwired/stimulus"
import { initializeApp } from "firebase/app"
import { getMessaging, register, unregister, onRegistered, onUnregistered, onMessage, isSupported } from "firebase/messaging"

// Browsers have no API to revoke a granted Notification permission, so an
// explicit opt-out is tracked here and consulted instead of re-trusting
// Notification.permission alone.
const DISABLED_KEY = "push-notifications-disabled"

// Registers this browser for FCM web push and keeps the token in sync with the
// server. Permission must be requested from a user gesture, hence the button.
export default class extends Controller {
  static values = { config: Object, vapidKey: String, enableLabel: String, disableLabel: String }
  static targets = ["toggle", "bellOff", "bellOn", "spinner"]

  async connect() {
    this.ready = false
    this.busy = false

    if (!(await this.supported())) {
      this.element.hidden = true
      return
    }

    this.messaging = getMessaging(initializeApp(this.configValue))
    this.ready = true

    // Foreground messages never reach the service worker, so render them here.
    onMessage(this.messaging, (payload) => {
      this.showForeground(payload)
      // Let the bell badge know a notification landed (background arrivals
      // are reported by the service worker instead, see notifications_controller.js).
      window.dispatchEvent(new CustomEvent("push:received"))
    })

    // The FID from `register()` arrives here, not as a return value, and
    // again on rotation, so this is the one place that keeps the server copy
    // current.
    onRegistered(this.messaging, (fid) => this.syncDevice(fid))

    // Fires once `unregister()` completes, and also if FCM invalidates our
    // FID on its own — either way the server copy needs to go too, so this
    // is the only place that removes it (see disable(), which just triggers
    // unregister() and waits for this).
    onUnregistered(this.messaging, (fid) => this.forgetDevice(fid))

    // Already granted on a previous visit: re-register silently to keep the
    // server in sync. Skipped if the user disabled notifications from within
    // the app, since permission itself can't be un-granted from here.
    if (Notification.permission === "granted" && !this.disabled) {
      this.subscribe()
    }

    this.render()
  }

  // Guarded by `busy` (not just `ready`) so a click while a previous
  // enable/disable is still in flight is ignored rather than racing it.
  async toggle() {
    if (!this.ready || this.busy) return

    this.busy = true
    this.render()

    if (Notification.permission === "granted" && !this.disabled) {
      await this.disable()
    } else {
      await this.enable()
    }

    this.busy = false
    this.render()
  }

  async enable() {
    const permission = await Notification.requestPermission()

    if (permission === "granted") {
      localStorage.removeItem(DISABLED_KEY)
      await this.subscribe()
    }
  }

  async disable() {
    localStorage.setItem(DISABLED_KEY, "1")
    await this.unsubscribe()
  }

  async subscribe() {
    try {
      const registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
      await navigator.serviceWorker.ready

      // Without an explicit registration the SDK looks for
      // /firebase-messaging-sw.js, which this app does not serve. The FID
      // itself arrives via the onRegistered listener set up in connect().
      await register(this.messaging, {
        vapidKey: this.vapidKeyValue,
        serviceWorkerRegistration: registration
      })
    } catch (error) {
      console.error("[push] registration failed", error)
    }
  }

  async unsubscribe() {
    try {
      // The removed FID arrives via the onUnregistered listener set up in
      // connect(), which is what actually clears the server copy.
      await unregister(this.messaging)
    } catch (error) {
      console.error("[push] unregistration failed", error)
    }
  }

  async syncDevice(fid) {
    try {
      await fetch("/device_tokens", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ token: fid })
      })
    } catch (error) {
      console.error("[push] device sync failed", error)
    }
  }

  async forgetDevice(fid) {
    try {
      await fetch("/device_tokens", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ token: fid })
      })
    } catch (error) {
      console.error("[push] device removal failed", error)
    }
  }

  // Shown via the SW registration (not `new Notification()`) so a click on it
  // fires the same service worker `notificationclick` handler as a background
  // push does — that's what marks the notification read.
  async showForeground(payload) {
    const data = payload.data || {}
    if (Notification.permission !== "granted") return

    const registration = await navigator.serviceWorker.ready
    registration.showNotification(data.title || "Timeline", {
      body: data.body,
      icon: "/icon.png",
      badge: "/icon.png",
      tag: data.tag || undefined,
      data: { path: data.path || "/", notificationId: data.notification_id || null }
    })
  }

  render() {
    if (!this.hasToggleTarget) return

    const active = Notification.permission === "granted" && !this.disabled

    this.toggleTarget.hidden = false
    this.toggleTarget.disabled = this.busy
    this.toggleTarget.setAttribute("aria-pressed", active)
    this.toggleTarget.title = active ? this.disableLabelValue : this.enableLabelValue

    if (this.hasBellOffTarget) this.bellOffTarget.classList.toggle("hidden", this.busy || active)
    if (this.hasBellOnTarget) this.bellOnTarget.classList.toggle("hidden", this.busy || !active)
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !this.busy)
  }

  get disabled() {
    return localStorage.getItem(DISABLED_KEY) === "1"
  }

  async supported() {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return false

    // iOS only allows web push from a home-screen installed PWA.
    return await isSupported()
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
